import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { KineticLine, FadeUp, Overline } from "../components/motion";
import { API } from "../constants/site";

const INTERESTS = ["Door knocking", "Lawn signs", "Phone calls", "Events", "Social media", "Anywhere I'm needed"];

const inputCls =
  "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

export default function GetInvolved() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleInterest = (item) =>
    setInterests((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, { ...form, interests });
      toast.success("Thank you for joining the campaign! Winnie's team will be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterests([]);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="get-involved-page" className="pt-32 lg:pt-40">
      <section className="mx-auto grid max-w-7xl gap-16 px-6 pb-28 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <Overline>Get Involved</Overline>
          <h1 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-ink sm:text-6xl">
            <KineticLine delay={0.1}>Join the</KineticLine>
            <KineticLine delay={0.22}>
              <span className="text-terracotta">movement.</span>
            </KineticLine>
          </h1>
          <FadeUp delay={0.3}>
            <p className="mt-8 text-base leading-relaxed text-stone2 md:text-lg">
              Campaigns are built by neighbours, not machines. Whether you have an hour a month or
              an hour a day, there is a place for you on this team.
            </p>
            <ul className="mt-10 space-y-5">
              {[
                "Knock on doors and meet your neighbours",
                "Host a lawn sign in your community",
                "Help at community events across Vaughan",
                "Spread the word online and in person",
              ].map((item, i) => (
                <li key={item} data-testid={`volunteer-perk-${i + 1}`} className="flex items-start gap-4 text-sm text-stone2 md:text-base">
                  <span className="mt-1 font-display font-bold text-terracotta">{String(i + 1).padStart(2, "0")}</span>
                  {item}
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="lg:col-span-7">
          <form
            data-testid="volunteer-form"
            onSubmit={submit}
            className="border border-sand bg-white p-8 lg:p-12"
          >
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
              Volunteer &amp; Supporter Signup
            </h2>
            <p className="mt-2 text-sm text-stone2">Tell us how you'd like to help.</p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="v-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Full Name *</label>
                <input id="v-name" data-testid="volunteer-name-input" required className={inputCls} placeholder="Your name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="v-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Email *</label>
                <input id="v-email" data-testid="volunteer-email-input" required type="email" className={inputCls} placeholder="you@example.com"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="v-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Phone (optional)</label>
                <input id="v-phone" data-testid="volunteer-phone-input" type="tel" className={inputCls} placeholder="(905) 555-0123"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>

            <fieldset className="mt-7">
              <legend className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-stone2">
                I'd like to help with
              </legend>
              <div data-testid="volunteer-interests" className="flex flex-wrap gap-2">
                {INTERESTS.map((item) => (
                  <button
                    type="button"
                    key={item}
                    data-testid={`volunteer-interest-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleInterest(item)}
                    aria-pressed={interests.includes(item)}
                    className={`rounded-sm border px-4 py-2 text-sm font-medium transition-all duration-300 active:scale-[0.97] ${
                      interests.includes(item)
                        ? "border-terracotta bg-terracotta text-cream"
                        : "border-stone-300 bg-white text-stone2 hover:border-terracotta hover:text-terracotta"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="mt-7">
              <label htmlFor="v-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Message (optional)</label>
              <textarea id="v-message" data-testid="volunteer-message-input" rows={4} className={inputCls}
                placeholder="Anything else you'd like us to know?"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>

            <button
              type="submit"
              data-testid="volunteer-submit-button"
              disabled={loading}
              className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-terracotta px-7 py-4 font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98] disabled:opacity-60 sm:w-auto"
            >
              {loading ? "Sending..." : "Count Me In"}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </FadeUp>
      </section>
    </main>
  );
}
